import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useListNeedsAttention } from "@/lib/api/risk/brokerage/tt/v2";
import { TtOrdersTable } from "./TtOrdersTable";
import { OrderSidePanel } from "./side-panel/OrderSidePanel";

const FOCUS_PARAM = "focus";

export function TtOrdersV2Tab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get(FOCUS_PARAM);

  const [selectedGuid, setSelectedGuid] = useState<string | null>(focusParam);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(focusParam != null);

  const needsAttentionResult = useListNeedsAttention();
  const rows = needsAttentionResult.data?.data?.rows ?? [];

  // Keep URL in sync with selection (for shareable links).
  useEffect(() => {
    if (selectedGuid && searchParams.get(FOCUS_PARAM) !== selectedGuid) {
      const next = new URLSearchParams(searchParams);
      next.set(FOCUS_PARAM, selectedGuid);
      setSearchParams(next, { replace: true });
    }
    if (!selectedGuid && searchParams.get(FOCUS_PARAM)) {
      const next = new URLSearchParams(searchParams);
      next.delete(FOCUS_PARAM);
      setSearchParams(next, { replace: true });
    }
  }, [selectedGuid, searchParams, setSearchParams]);

  const handleOpen = useCallback((guid: string) => {
    setSelectedGuid(guid);
    setIsPanelOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedGuid(null);
  }, []);

  const wasResolved = useMemo(() => {
    if (!selectedGuid) return false;
    if (needsAttentionResult.isLoading) return false;
    return !rows.some((r: typeof rows[number]) => r.guid_order_id === selectedGuid);
  }, [selectedGuid, rows, needsAttentionResult.isLoading]);

  return (
    <div className="flex flex-col gap-3 h-full overflow-hidden">
      {/* Header strip */}
      <div className="flex flex-row items-center justify-end gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => needsAttentionResult.refetch()}
          disabled={needsAttentionResult.isFetching}
        >
          {needsAttentionResult.isFetching ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      {needsAttentionResult.isError && (
        <div className="p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-700">
          Failed to load orders.
          {needsAttentionResult.error instanceof Error
            ? ` ${needsAttentionResult.error.message}`
            : ""}
        </div>
      )}

      {/* Body: table + optional side panel */}
      <div className="flex flex-row gap-4 flex-1 min-h-0 overflow-hidden">
        <TtOrdersTable
          rows={rows}
          selectedGuid={selectedGuid}
          onOpen={handleOpen}
          isLoading={needsAttentionResult.isLoading}
        />

        {isPanelOpen && (
          <div className="w-[480px] shrink-0 border rounded-md bg-card overflow-hidden flex flex-col">
            <OrderSidePanel
              guidOrderId={selectedGuid}
              wasResolved={wasResolved}
              onClose={handleClose}
            />
          </div>
        )}
      </div>
    </div>
  );
}
